import {
  DARK_SHOP_PRICES,
  DARK_SHOP_TOWERS,
  LIGHT_SHOP_PRICES,
  LIGHT_SHOP_TOWERS,
} from '../../config/originShop';
import Phaser from 'phaser';
import {
  HUB_CHAPTERS,
  HUB_LEVELS,
  MOON_LEVELS,
  DIAMOND_LEVEL_ID,
  DEBRIS_LEVEL_ID,
  MOONSTONE_LEVEL_ID,
} from '../../config/hubLevels';
import type { EffectKind } from '../../config/effects';
import { RESTOCK_BANNER_SENDER, RESTOCK_BANNER_TEXT } from '../../ui/restockWatch';
import { hideBootSplash } from '../bootSplash';
import { LEVELS, CHAPTERS, LAST_LEVEL_ID, levelsInChapter, hasBoss } from '../../config/levels';
import {
  ARENA_LEVEL_ID,
  CLOUD_ARENA_LEVEL_ID,
  FUNGI_ARENA_LEVEL_ID,
  MOON_ARENA_LEVEL_ID,
  SHADOW_ARENA_LEVEL_ID,
} from '../../config/testArena';
import { BALANCE } from '../../config/balance';
import { TOWER_NAMES, ENEMY_NAMES } from '../../config/displayNames';
import { CODES_PURPLE, openCodesPanel } from '../codesPanel';
import { FEEDBACK_GREEN, openFeedbackPanel } from '../feedbackPanel';
import { showCodeRewardBanner } from '../../ui/codeRewardBanner';
import { showAccountPanel } from '../../account/accountUi';
import { enqueueBanner } from '../../ui/messageBanner';
import { enqueueDevPopup } from '../../ui/devPopup';
import { enqueueAchievementToast } from '../../ui/achievementToast';
import { TOWER_ORDER, ENEMY_ORDER } from '../../config/reviewOrder';
import { CAPSULE_ODDS, CAPSULE_TOWERS } from '../../config/capybaraEvent';
import { openGoldenBag } from '../bagOpening';
import { ORIGIN_CHAPTERS, EVIL_ORB_DECOR, HEAVENS_PEAK_RING } from '../../config/originEvent';
import { LIGHT_ORB_LOOK, playEvilOrbCutscene, playOrbCutscene } from '../evilOrbCutscene';
import { openChest } from '../chestOpening';
import type { TowerType } from '../../core/types';
import {
  TOWER_UNLOCKS,
  SHOP_TOWERS,
  SHOP_SALE_TOWERS,
  MARKET_TOWERS,
  SHOP_PRICES,
  TOWER_GRANTS_ON_WIN,
} from '../../config/unlocks';
import {
  BAG_COST,
  BAG_PREP_MS,
  DUPLICATE_REFUND,
  GOLDEN_BAG_COST,
  GOLDEN_BAG_PREP_MS,
  GOLDEN_BAG_TOWERS,
  GOLDEN_DUPLICATE_REFUND,
  TAKEOUT_BAG_ODDS,
  oddsLabel,
} from '../../config/restaurant';
import { SHREK_CHALLENGE } from '../../core/arenaChallenge';
import { towerSpecial } from '../towerInfo';
import { spinCoin } from '../coin';
import { playVarietyOrbCutscene } from '../varietyOrbCutscene';
import { playPrinceNachoCutscene } from '../princeNachoCutscene';
import { playShroometronCutscene } from '../shroometronCutscene';
import { openVarietyOrb } from '../varietyOrbOpening';
import { ProgressService } from '../../services/progressService';
import { RankService } from '../../services/rankService';
import { MAX_RANK } from '../../services/rankMath';
import { QuestService } from '../../services/questService';
import { STAT_FIELDS } from '../../config/lifetimeStats';
import { saveStorage } from '../saveStorage';

/**
 * DemoScene — secret debug/review screen (reached via `?debug`; see BootScene).
 *
 * Four tabs:
 *  - ASSETS:   a labelled grid of every texture currently loaded. Click any tile
 *              to view that graphic at 1:1 (native pixel size). Includes a live
 *              spinning-coin sample.
 *  - SCREENS:  jump buttons to every screen — Menu, Level Select, Shop, Equip,
 *              each level's gameplay, and forced Level-Complete / Game-Over
 *              overlays (review mode: these persist nothing — no scores, stars,
 *              unlocks, or coins).
 *  - STATS:    the full balance table — tower costs / damage / fire-rate / range
 *              per tier with specials, the damage rule, and enemy hp/speed/bounty.
 *  - CHAPTERS: per-chapter breakdown — coin reward, wave count, starting cash /
 *              lives and enemy roster for every episode, plus each chapter's
 *              total coin payout.
 *
 * Not part of normal play; never reachable without the secret query string.
 */
const GOLD = 0xf5c518;
const GOLD_HEX = '#f5c518';

/** Phaser-internal texture keys to hide from the gallery. */
const INTERNAL_KEYS = new Set(['__DEFAULT', '__MISSING', '__WHITE', '__BASE']);

interface TabDef {
  name: 'assets' | 'screens' | 'stats' | 'chapters' | 'hub' | 'events';
  container: Phaser.GameObjects.Container;
  bg: Phaser.GameObjects.Rectangle;
  txt: Phaser.GameObjects.Text;
}

export class DemoScene extends Phaser.Scene {
  private tabs: TabDef[] = [];
  /** The 1:1 fullscreen viewer overlay, when open. */
  private viewer: Phaser.GameObjects.Container | null = null;

  constructor() {
    super({ key: 'DemoScene' });
  }

  create(): void {
    hideBootSplash();
    this.tabs = [];
    this.viewer = null;

    const W = 1280;
    this.add.rectangle(W / 2, 360, W, 720, 0x0c0b16, 1);
    this.add
      .text(24, 24, 'DEMO MODE', { fontSize: '26px', color: GOLD_HEX, fontStyle: 'bold' })
      .setOrigin(0, 0.5);
    this.add
      .text(W - 24, 24, 'reached via ?debug · review only — nothing is saved', {
        fontSize: '13px',
        color: '#7f8c8d',
      })
      .setOrigin(1, 0.5);

    // Build all tab contents.
    const assets = this.add.container(0, 0);
    const screens = this.add.container(0, 0);
    const stats = this.add.container(0, 0);
    const chapters = this.add.container(0, 0);
    const hub = this.add.container(0, 0);
    const events = this.add.container(0, 0);
    this.buildAssets(assets);
    this.buildScreens(screens);
    this.buildStats(stats);
    this.buildChapters(chapters);
    this.buildHub(hub);
    this.buildEvents(events);

    // Tab switcher.
    this.makeTab(70, 64, 'ASSETS', 'assets', assets);
    this.makeTab(200, 64, 'SCREENS', 'screens', screens);
    this.makeTab(330, 64, 'STATS', 'stats', stats);
    this.makeTab(460, 64, 'CHAPTERS', 'chapters', chapters);
    this.makeTab(590, 64, 'HUB', 'hub', hub);
    this.makeTab(720, 64, 'EVENTS', 'events', events);
    this.showTab('assets');

    // Esc closes the 1:1 viewer if open.
    this.input.keyboard?.on('keydown-ESC', () => this.closeViewer());
  }

  private showTab(which: TabDef['name']): void {
    for (const t of this.tabs) {
      const active = t.name === which;
      t.container.setVisible(active);
      t.bg.setFillStyle(active ? 0x2a2640 : 0x17151f);
      t.bg.setStrokeStyle(2, active ? GOLD : 0x44485a);
      t.txt.setColor(active ? GOLD_HEX : '#cfd3dc');
    }
  }

  private makeTab(
    x: number,
    y: number,
    label: string,
    name: TabDef['name'],
    container: Phaser.GameObjects.Container,
  ): void {
    const bg = this.add
      .rectangle(x, y, 120, 34, 0x17151f)
      .setStrokeStyle(2, 0x44485a)
      .setInteractive({ useHandCursor: true });
    const txt = this.add.text(x, y, label, { fontSize: '15px', color: '#cfd3dc' }).setOrigin(0.5);
    bg.on('pointerup', () => {
      this.closeViewer();
      this.showTab(name);
    });
    this.tabs.push({ name, container, bg, txt });
  }

  // ── Assets tab ─────────────────────────────────────────────────────────────

  private buildAssets(parent: Phaser.GameObjects.Container): void {
    const keys = this.textures
      .getTextureKeys()
      .filter((k) => !INTERNAL_KEYS.has(k))
      .sort();

    const startX = 70;
    const startY = 120;
    const cols = 12;
    const cellW = 98;
    // Shrink rows when the texture count grows so the grid plus the sample
    // row below it always stays inside the 720px canvas.
    const rows = Math.ceil(keys.length / cols);
    const cellH = Math.min(104, Math.floor((720 - startY - 48) / rows));
    // Everything inside a cell is PROPORTIONAL to its height: at 281 textures
    // a row is 23px, and fixed offsets (a label 30px below the centre) spilled
    // the last row into the footer — the overlap e2e caught it on CI's fonts.
    const thumb = Math.max(10, Math.min(60, Math.floor(cellH * 0.45)));
    const imgDy = -Math.round(cellH * 0.15);
    const labelDy = Math.round(cellH * 0.3);

    keys.forEach((key, i) => {
      const cx = startX + (i % cols) * cellW + cellW / 2;
      const cy = startY + Math.floor(i / cols) * cellH + cellH / 2;

      const frame = this.add
        .rectangle(cx, cy, cellW - 10, cellH - 4, 0x15131f, 1)
        .setStrokeStyle(1, 0x33384a)
        .setInteractive({ useHandCursor: true });
      frame.on('pointerover', () => frame.setStrokeStyle(2, GOLD));
      frame.on('pointerout', () => frame.setStrokeStyle(1, 0x33384a));
      frame.on('pointerup', () => this.showAssetFullsize(key));
      parent.add(frame);

      const img = this.add.image(cx, cy + imgDy, key);
      const src = this.textures.get(key).getSourceImage() as { width: number; height: number };
      const scale = Math.min(thumb / (src.width || thumb), thumb / (src.height || thumb));
      img.setScale(scale);
      parent.add(img);

      const label = this.add
        .text(cx, cy + labelDy, key, { fontSize: '9px', color: '#9aa6ad', align: 'center' })
        .setOrigin(0.5);
      if (label.width > cellW - 6) label.setScale((cellW - 6) / label.width);
      parent.add(label);
    });

    // Lay out label → coin → count sequentially off measured widths so they
    // never overlap regardless of the label's rendered length.
    const sampleY = startY + (Math.floor((keys.length - 1) / cols) + 1) * cellH + 24;
    const sampleLabel = this.add
      .text(startX, sampleY, 'click any tile to view at 1:1 · live spinCoin():', {
        fontSize: '13px',
        color: '#cfd3dc',
      })
      .setOrigin(0, 0.5);
    parent.add(sampleLabel);

    const coinX = sampleLabel.x + sampleLabel.width + 26;
    const liveCoin = this.add.image(coinX, sampleY, 'coin').setDisplaySize(40, 40);
    spinCoin(this, liveCoin);
    parent.add(liveCoin);

    parent.add(
      this.add
        .text(coinX + 34, sampleY, `${keys.length} textures loaded`, {
          fontSize: '12px',
          color: '#7f8c8d',
        })
        .setOrigin(0, 0.5),
    );
  }

  /** Fullscreen 1:1 (native pixel size) view of a single texture. */
  private showAssetFullsize(key: string): void {
    this.closeViewer();
    const W = 1280;
    const H = 720;
    const src = this.textures.get(key).getSourceImage() as { width: number; height: number };

    const backdrop = this.add
      .rectangle(W / 2, H / 2, W, H, 0x000000, 0.92)
      .setInteractive({ useHandCursor: true });
    backdrop.on('pointerup', () => this.closeViewer());

    const img = this.add.image(W / 2, H / 2 + 16, key).setScale(1); // native size
    const caption = this.add
      .text(W / 2, 40, `${key} — ${src.width}×${src.height} @ 1:1   (click / Esc to close)`, {
        fontSize: '16px',
        color: GOLD_HEX,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.viewer = this.add.container(0, 0, [backdrop, img, caption]).setDepth(1000);
  }

  private closeViewer(): void {
    if (this.viewer) {
      this.viewer.destroy();
      this.viewer = null;
    }
  }

  // ── Screens tab ────────────────────────────────────────────────────────────

  private buildScreens(parent: Phaser.GameObjects.Container): void {
    parent.add(
      this.add.text(
        70,
        112,
        'Jump to any screen. Menu / Level Select / levels are live — press Esc on any screen to return here.\n' +
          'Overlay buttons run in review mode: the win/loss screens show with no progress saved.',
        { fontSize: '13px', color: '#9aa6ad', lineSpacing: 4 },
      ),
    );

    const mk = (x: number, y: number, label: string, color: number, onClick: () => void) =>
      parent.add(this.makeButton(x, y, label, color, onClick));

    const colX = [120, 360, 600];
    // Row pitch ADAPTS to the campaign size. The grid is a nav row, one row per
    // four episodes, then two overlay rows; at a fixed 64px pitch, 22 episodes
    // put the last overlay row at y=712, so its 48px-tall button hung off the
    // bottom of the canvas and its label sat at 720.7 — passing the e2e only on
    // its ±1px tolerance, one font-metric change away from failing on CI.
    // Deriving the pitch keeps the last row on-canvas as more episodes land.
    const ROW_TOP = 200;
    const ROW_LAST = 686; // last row centre: a 48px button ends at 710 < 720
    const lastRow = 1 + Math.ceil(LEVELS.length / 4) + 5; // nav + grid + 6 overlay rows
    const rowPitch = Math.min(64, (ROW_LAST - ROW_TOP) / lastRow);
    const rowY = (r: number) => ROW_TOP + r * rowPitch;

    mk(colX[0], rowY(0), 'Menu', 0x2a2a7a, () => this.scene.start('MenuScene'));
    mk(colX[1], rowY(0), 'Level Select', 0x2a2a7a, () => this.scene.start('LevelSelectScene'));
    mk(colX[2], rowY(0), 'Shop', 0x2a2a7a, () => this.scene.start('ShopScene'));
    // Equip sits beside Shop on the nav row (its own 4th column, so the level
    // grid below stays on 3 columns).
    mk(840, rowY(0), 'Equip', 0x2a2a7a, () => this.scene.start('EquipScene'));
    // The Test Arena — a 5th nav column rather than a row under the level grid,
    // which already reaches the bottom of the canvas.
    mk(1080, rowY(0), 'Test Arena', 0x1a3a7a, () => {
      this.scene.start('GameScene', { levelId: ARENA_LEVEL_ID });
      this.scene.launch('HudScene', { levelId: ARENA_LEVEL_ID, arena: true });
    });

    // The level grid runs FOUR columns (the nav row above stays on its own
    // layout) — at 20 episodes a 3-column grid pushed the overlay rows below
    // the canvas bottom (rowY(8) = 712 + half a button > 720).
    const gridX = [120, 360, 600, 840];
    LEVELS.forEach((l, i) => {
      const x = gridX[i % 4];
      const y = rowY(1 + Math.floor(i / 4));
      const label = `Play C${l.chapter ?? 1}·${l.episode ?? l.id}`;
      mk(x, y, label, 0x1a6b1a, () => {
        this.scene.start('GameScene', { levelId: l.id });
        this.scene.launch('HudScene', { levelId: l.id, debug: true });
      });
    });

    // King Hambi (spec 2026-09-22), down the free fifth column beside the level
    // grid — no new row, so the overlap e2e keeps its Linux-font headroom.
    // Writes land in the ?debug sandbox.
    mk(1080, rowY(1), 'King Hambi prize', 0xc62a42, () =>
      this.scene.start('GameScene', { levelId: 25, demo: 'win' }),
    );
    mk(1080, rowY(2), 'King Hambi · arena', 0xc62a42, () => {
      // Granted in the sandbox, then the arena: place him, send a wave, and
      // his K–O goes off 30 s later (2x on the HUD halves the wait).
      new ProgressService(saveStorage(), LAST_LEVEL_ID).grantTower('kinghambi');
      this.scene.start('GameScene', { levelId: ARENA_LEVEL_ID });
      this.scene.launch('HudScene', { levelId: ARENA_LEVEL_ID, arena: true });
    });
    mk(1080, rowY(3), 'Storage · gold', 0xffb81c, () => {
      new ProgressService(saveStorage(), LAST_LEVEL_ID).awardGoldstone(3);
      this.scene.start('StorageScene', { from: 'DemoScene' });
    });
    // The Golden Takeout Bag (spec 2026-09-26), further down the same free
    // fifth column. Writes land in the ?debug sandbox.
    const golden = () => new ProgressService(saveStorage(), LAST_LEVEL_ID);
    mk(1080, rowY(4), 'Golden bag · ready', 0xb8860b, () => {
      const p = golden();
      p.clearGoldenBagOrder();
      p.addCoins(GOLDEN_BAG_COST);
      p.orderGoldenBag(GOLDEN_BAG_COST, Date.now() - GOLDEN_BAG_PREP_MS - 1000);
      this.scene.start('RestaurantScene', { page: 1 });
    });
    mk(1080, rowY(5), 'Golden bag · cooking', 0xb8860b, () => {
      const p = golden();
      p.clearGoldenBagOrder();
      p.addCoins(GOLDEN_BAG_COST);
      p.orderGoldenBag(GOLDEN_BAG_COST, Date.now());
      this.scene.start('RestaurantScene', { page: 1 });
    });
    mk(1080, rowY(6), 'Open golden bag', 0xb8860b, () => {
      openGoldenBag(this, { progress: golden(), onDone: () => undefined });
    });
    mk(1080, rowY(7), 'Storage · golden', 0xb8860b, () => {
      const p = golden();
      p.addCoins(GOLDEN_BAG_COST * 2);
      for (let i = 0; i < 2; i++) {
        p.orderGoldenBag(GOLDEN_BAG_COST, 0);
        p.storeGoldenBag();
      }
      this.scene.start('StorageScene', { from: 'DemoScene' });
    });
    mk(1080, rowY(8), 'Golden towers · arena', 0xb8860b, () => {
      // All six owned and equipped, in the Test Arena.
      const p = golden();
      for (const t of GOLDEN_BAG_TOWERS) p.awardTower(t as Parameters<typeof p.awardTower>[0]);
      p.setEquipped([...GOLDEN_BAG_TOWERS], [...GOLDEN_BAG_TOWERS]);
      this.scene.start('GameScene', { levelId: ARENA_LEVEL_ID });
      this.scene.launch('HudScene', { levelId: ARENA_LEVEL_ID, arena: true });
    });

    const overlayRow = 1 + Math.ceil(LEVELS.length / 4);
    // Redeem codes (spec 2026-10-09), in the level grid's last row — 25
    // episodes leave its last three columns free. The panel asks to sign in
    // here (no session in the sandbox); the banner shows a sample payout.
    const lastGridRow = Math.ceil(LEVELS.length / 4);
    if (LEVELS.length % 4 !== 0 && LEVELS.length % 4 <= 2) {
      mk(gridX[2], rowY(lastGridRow), 'Codes panel', CODES_PURPLE, () => openCodesPanel(this));
      mk(gridX[3], rowY(lastGridRow), 'Code banner', CODES_PURPLE, () =>
        showCodeRewardBanner(['300 Coins', '5 × Golden Stone', '2 × Chili', 'Ninja Hambi']),
      );
    }
    mk(gridX[0], rowY(overlayRow), 'Win overlay', 0x7a5a1a, () =>
      this.scene.start('GameScene', { levelId: LEVELS[0].id, demo: 'win' }),
    );
    mk(gridX[1], rowY(overlayRow), 'Loss overlay', 0x7a1a1a, () =>
      this.scene.start('GameScene', { levelId: LEVELS[0].id, demo: 'loss' }),
    );
    // Boss finales get their own celebratory overlays.
    mk(gridX[2], rowY(overlayRow), 'Boss win (Gold)', 0x7a5a1a, () =>
      this.scene.start('GameScene', { levelId: 5, demo: 'win' }),
    );
    // The arena's own loss screen ("Too Risky!" + defeated/spawned scoreboard).
    mk(gridX[3], rowY(overlayRow), 'Arena loss', 0x7a1a1a, () =>
      this.scene.start('GameScene', { levelId: ARENA_LEVEL_ID, demo: 'loss' }),
    );
    mk(gridX[0], rowY(overlayRow + 1), 'Boss win (Null)', 0x4a4a55, () =>
      this.scene.start('GameScene', { levelId: 10, demo: 'win' }),
    );
    mk(gridX[1], rowY(overlayRow + 1), 'Boss win (King)', 0xffd23f, () =>
      this.scene.start('GameScene', { levelId: 15, demo: 'win' }),
    );
    mk(gridX[2], rowY(overlayRow + 1), 'Boss win (Emperor)', 0xb2140c, () =>
      this.scene.start('GameScene', { levelId: 20, demo: 'win' }),
    );
    // The in-game "?" tower guide, opened over the last level's full roster.
    const lastId = LEVELS[LEVELS.length - 1].id;
    mk(gridX[3], rowY(overlayRow + 1), 'Tower Guide (?)', 0x1a5a6b, () => {
      this.scene.start('GameScene', { levelId: lastId });
      this.scene.launch('HudScene', { levelId: lastId, debug: true, help: true });
    });
    // The Tier-4 Evolution lab (normally gated behind clearing Chapter 2).
    // The Event Archive (spec 2026-10-04), in the fifth column's free cells
    // beside these overlay rows — no new row, so the overlap e2e keeps its
    // headroom. The short run's LEADERBOARD button opens the Capy Run board.
    mk(1080, rowY(overlayRow + 1), 'Event Archive', 0x1f5f8b, () => {
      const s = saveStorage();
      s.setItem('hambi.flags', JSON.stringify(['hub', 'potionshop']));
      this.scene.start('HubScene', { tab: 'events', archive: true });
    });
    mk(1080, rowY(overlayRow + 2), 'Archive run (12)', 0x1f5f8b, () =>
      this.scene.start('CapybaraScene', {
        mode: 'archive',
        total: 12,
        returnTo: 'DemoScene',
        returnData: {},
      }),
    );
    mk(1080, rowY(overlayRow + 3), 'Storage · capy', 0xa8703a, () => {
      new ProgressService(saveStorage(), LAST_LEVEL_ID).awardCapystone(3);
      this.scene.start('StorageScene', { from: 'DemoScene' });
    });
    mk(gridX[0], rowY(overlayRow + 2), 'Evolve', 0x1a6b3a, () => this.scene.start('EvolveScene'));
    // The campaign finale's win screen — the only one that rains confetti.
    mk(gridX[1], rowY(overlayRow + 2), 'Boss win (Ember)', 0x2f8fe6, () =>
      this.scene.start('GameScene', { levelId: 25, demo: 'win' }),
    );
    // The Restaurant (order a takeout bag) and its Storage shelf.
    mk(gridX[2], rowY(overlayRow + 2), 'Restaurant', 0x7a5a1a, () =>
      this.scene.start('RestaurantScene'),
    );
    mk(gridX[3], rowY(overlayRow + 2), 'Storage', 0x4a4f57, () => this.scene.start('StorageScene'));
    // The optional account system's DOM overlay (top-right of the page, above
    // the canvas). No-ops when the build carries no Supabase env — the buttons
    // still document that the feature exists.
    mk(gridX[0], rowY(overlayRow + 3), 'Acct: register', 0x3a2a7a, () =>
      showAccountPanel('register'),
    );
    mk(gridX[1], rowY(overlayRow + 3), 'Acct: login', 0x3a2a7a, () => showAccountPanel('login'));
    mk(gridX[2], rowY(overlayRow + 3), 'Acct: profile', 0x3a2a7a, () =>
      showAccountPanel('profile'),
    );
    mk(gridX[3], rowY(overlayRow + 3), 'Acct: admin', 0x3a2a7a, () => showAccountPanel('admin'));
    // Dev-command delivery banner (see src/ui/messageBanner.ts) — a manual
    // trigger to review its on-screen appearance without a live /message.
    mk(gridX[0], rowY(overlayRow + 4), 'Dev banner', 0x3a2a7a, () =>
      enqueueBanner('Hambi', 'Hi Everyone'),
    );
    // Higher-dev senders render blue (round 3).
    mk(gridX[1], rowY(overlayRow + 4), 'Banner (DEV+)', 0x2f8fe6, () =>
      enqueueBanner('Hambi', 'Hi Everyone', 'higher_dev'),
    );
    // Every dev popup variant queued back to back — press OK to step through.
    mk(gridX[2], rowY(overlayRow + 4), 'Dev popups', 0x3a2a7a, () => {
      enqueueDevPopup('coins');
      enqueueDevPopup('bags');
      enqueueDevPopup('unlock');
      enqueueDevPopup('icon');
      enqueueDevPopup('op');
      enqueueDevPopup('ban');
    });
    // Leaderboards (2026-09-20). Both free cells of this row — gridX[3] and a
    // fifth column — so no new row is added and the adaptive row pitch below
    // stays put (see the ROW_LAST note above and the DemoScene overlap e2e).
    // Without VITE_SUPABASE_* the board renders its LEADERBOARD UNAVAILABLE
    // state, which is itself the thing worth reviewing here.
    mk(gridX[3], rowY(overlayRow + 4), 'Leaderboard', 0x5d7a4a, () =>
      this.scene.start('LeaderboardScene'),
    );
    mk(1080, rowY(overlayRow + 4), 'Avatar picker', 0x5d7a4a, () =>
      this.scene.start('AvatarScene'),
    );
    // Ranks & achievements: the scene itself, and the toast in its three
    // dressings (earned, /give achieve, /give rank) queued back to back.
    mk(gridX[0], rowY(overlayRow + 5), 'Achievements', 0x8a6d00, () =>
      this.scene.start('AchievementsScene'),
    );
    mk(gridX[1], rowY(overlayRow + 5), 'Ach toasts', 0x8a6d00, () => {
      enqueueAchievementToast({
        title: 'First Kill',
        body: 'Defeat your first enemy',
        iconKey: 'ach-firstkill',
      });
      enqueueAchievementToast({
        title: 'You have been awarded',
        body: 'Stageness',
        iconKey: 'ach-stageness',
      });
      enqueueAchievementToast({
        title: 'Your rank has been updated',
        body: '',
        iconKey: 'medal-gold',
        overlay: '?',
      });
      // Batch 2: /give achieve all and reset dressings.
      enqueueAchievementToast({
        title: 'You have been awarded',
        body: 'All achievements',
        iconKey: 'medal-gold',
      });
      enqueueAchievementToast({
        title: 'Your achievements have been reset',
        body: '',
        iconKey: 'medal-bronze',
        overlay: '✕',
      });
      // Diamond (2026-10-29): a Diamond badge, then the Diamond medal.
      enqueueAchievementToast({
        title: 'King of the board',
        body: 'Reach #1 on any leaderboard',
        iconKey: 'ach-kingoftheboard',
      });
      enqueueAchievementToast({ title: 'Diamond Tier', body: 'Rank 31', iconKey: 'medal-diamond' });
    });
    // The Variety Orb (Level 25's prize). Both halves of its ceremony, on the
    // two free columns of this row — normally each is seen once per save, so
    // the debug page is the only practical way to review them.
    // Writes land in the ?debug sandbox, so opening one here grants nothing real.
    mk(gridX[2], rowY(overlayRow + 5), 'Orb cutscene', 0x8a3dc7, () => {
      playVarietyOrbCutscene(this, () => {});
    });
    // The Avatar Shop (2026-09-21) — the fifth column of this row, again so no
    // new row appears and the overlap e2e keeps its Linux-font headroom.
    mk(1080, rowY(overlayRow + 5), 'Avatar shop', 0x5d7a4a, () =>
      this.scene.start('AvatarShopScene'),
    );
    mk(gridX[3], rowY(overlayRow + 5), 'Orb reveal', 0x8a3dc7, () => {
      openVarietyOrb(this, {
        progress: new ProgressService(saveStorage(), LAST_LEVEL_ID),
        onDone: () => {},
      });
    });
  }

  // ── Hub tab ────────────────────────────────────────────────────────────────

  /**
   * The Hub (spec 2026-09-02-the-hub-design.md): its screens, its five levels,
   * the Diamond claim, and the timed-effect dressing — none of which a fresh
   * save can reach without beating the campaign and reaching rank 10. Writes
   * land in the ?debug sandbox, so nothing here touches the real save.
   */
  private buildHub(parent: Phaser.GameObjects.Container): void {
    parent.add(
      this.add.text(
        70,
        112,
        'THE HUB — screens, levels and effects. The sandbox grants the gates for this page load only.\n' +
          'Potions / Diamond buttons start real timers in the sandbox save: watch the bottom-right squares.',
        { fontSize: '13px', color: '#9aa6ad', lineSpacing: 4 },
      ),
    );
    const mk = (x: number, y: number, label: string, color: number, onClick: () => void) =>
      parent.add(this.makeButton(x, y, label, color, onClick));
    const colX = [120, 360, 600, 840, 1080];
    // Nine rows (the Origin Event and the capsules moved to the EVENTS tab on
    // 2026-10-08, when its nine levels outgrew two rows here). The pitch is
    // DERIVED rather than fixed at 64 — the same trick the Levels tab uses —
    // so a tenth row can never hang off the canvas and fail the overlap e2e.
    const HUB_ROW_TOP = 196;
    const HUB_ROW_LAST = 686; // last row centre: a 48px button ends at 710 < 720
    const HUB_LAST_ROW = 10;
    const hubPitch = Math.min(64, (HUB_ROW_LAST - HUB_ROW_TOP) / HUB_LAST_ROW);
    const rowY = (r: number) => HUB_ROW_TOP + r * hubPitch;
    const progress = () => new ProgressService(saveStorage(), LAST_LEVEL_ID);
    const unlockGates = () => {
      // Both gates, through their dev flags, in the sandbox.
      const s = saveStorage();
      const flags = new Set<string>();
      try {
        for (const f of JSON.parse(s.getItem('hambi.flags') ?? '[]') as unknown[]) {
          if (typeof f === 'string') flags.add(f);
        }
      } catch {
        /* fresh */
      }
      flags.add('hub');
      flags.add('potionshop');
      s.setItem('hambi.flags', JSON.stringify([...flags]));
    };

    mk(colX[0], rowY(0), 'Unlock gates', 0x7a1a1a, () => {
      unlockGates();
      enqueueBanner('Debug', 'Hub + Potion Shop unlocked (sandbox)');
    });
    mk(colX[1], rowY(0), 'Hub menu', 0xc0392b, () => {
      unlockGates();
      this.scene.start('HubScene');
    });
    mk(colX[2], rowY(0), 'Hub · Market', 0x1e5fa8, () => {
      unlockGates();
      this.scene.start('HubScene', { tab: 'market' });
    });
    mk(colX[3], rowY(0), 'Hub · Potions', 0x2e7d32, () => {
      unlockGates();
      progress().addCoins(1000);
      this.scene.start('HubScene', { tab: 'potions' });
    });
    mk(colX[4], rowY(0), 'The Moon', 0x4a4f8a, () => {
      unlockGates();
      this.scene.start('HubChapterScene', { chapter: 1 });
    });

    // Play buttons — levels 1–5 on one row, 6–10 on the row under the quests.
    MOON_LEVELS.forEach((l, i) => {
      const [px, py] = i < 5 ? [colX[i], rowY(1)] : [colX[i - 5], rowY(6)];
      // Short names fit the 210px button: "Constellation Valley" is its first word.
      const short = l.name.replace(/^The /, '');
      const label = short.length > 16 ? short.split(' ')[0] : short;
      mk(px, py, `Play ${label}`, 0x1a6b1a, () => {
        this.scene.start('GameScene', { levelId: l.id });
        this.scene.launch('HudScene', { levelId: l.id, debug: true });
      });
    });
    mk(colX[0], rowY(2), 'Diamond claim', 0x8a6d00, () => {
      // A real (sandboxed) first win of HTD Exhibition: the claim modal shows.
      const p = progress();
      p.setHubCompleted(1);
      this.scene.start('GameScene', { levelId: DIAMOND_LEVEL_ID });
      this.scene.launch('HudScene', { levelId: DIAMOND_LEVEL_ID, debug: true });
    });
    mk(colX[1], rowY(2), 'Tower picker', 0x2e7d32, () => {
      this.scene.start('TowerPickScene', { kind: 'pickle', returnTo: 'DemoScene' });
    });
    mk(colX[2], rowY(2), 'Storage (hub)', 0x4a4f57, () => {
      const p = progress();
      p.claimDiamond();
      p.storePotion('pickle');
      p.storePotion('burger');
      this.scene.start('StorageScene', { from: 'DemoScene' });
    });

    // Effects: start each timer in the sandbox so the corner squares, tower
    // icons and the Diamond's blue star can be reviewed in any level.
    const start = (kind: EffectKind, tower?: TowerType) => () => {
      const r = progress().startEffect(kind, tower, Date.now());
      enqueueBanner('Debug', r.ok ? `${kind} started` : `${kind}: ${r.reason}`);
    };
    mk(colX[0], rowY(3), 'Diamond 1:00', 0x8a6d00, start('diamond'));
    mk(colX[1], rowY(3), 'Pickle → Hambi', 0x2e7d32, start('pickle', 'patty'));
    mk(colX[2], rowY(3), 'Chili → Cheese', 0x2e7d32, start('chili', 'cheese'));
    mk(colX[3], rowY(3), 'COLA → Hambi', 0x2e7d32, start('cola', 'patty'));
    mk(colX[4], rowY(3), 'Burger (all)', 0x2e7d32, start('burger'));
    mk(colX[0], rowY(4), 'Clear effects', 0x4a4f57, () => {
      progress().setEffects([], Date.now());
      enqueueBanner('Debug', 'effects cleared');
    });
    mk(colX[1], rowY(4), 'Restock banner', 0x3a2a7a, () =>
      enqueueBanner(RESTOCK_BANNER_SENDER, RESTOCK_BANNER_TEXT),
    );
    mk(colX[2], rowY(4), 'Moon (peaceful)', 0x2e7d32, () => {
      unlockGates();
      progress().setPeacefulMode(true);
      this.scene.start('HubChapterScene', { chapter: 1 });
    });
    mk(colX[3], rowY(4), 'Events tab', 0x8a6d00, () => {
      unlockGates();
      this.scene.start('HubScene', { tab: 'events' });
    });
    // The Capybara Event's minigame (spec 2026-09-15), straight in; LEAVE
    // and BACK TO EVENTS return here rather than to the Hub.
    mk(colX[4], rowY(6), 'Capy minigame', 0x8a5430, () => {
      this.scene.start('CapybaraScene', { returnTo: 'DemoScene' });
    });
    mk(colX[4], rowY(4), 'Market · locked', 0x1e5fa8, () => {
      // The hub gate open, the rank-10 gate shut: only the `hub` flag, rank 1.
      const s = saveStorage();
      s.setItem('hambi.flags', JSON.stringify(['hub']));
      s.setItem('hambi.rank', '1');
      this.scene.start('HubScene', { tab: 'market' });
    });
    mk(colX[3], rowY(2), 'Moon Arena', 0x8a1a1a, () => {
      this.scene.start('GameScene', { levelId: MOON_ARENA_LEVEL_ID });
      this.scene.launch('HudScene', { levelId: MOON_ARENA_LEVEL_ID, arena: 'moon' });
    });
    mk(colX[4], rowY(2), 'Potion popup', 0x3a2a7a, () =>
      enqueueDevPopup('potion', { art: 'potion-chili' }),
    );

    // Revision 9: the QUESTS tab and the two new potions.
    mk(colX[0], rowY(5), 'Hub · Quests', 0xb35f10, () => {
      unlockGates();
      this.scene.start('HubScene', { tab: 'quests' });
    });
    mk(colX[1], rowY(5), 'Samurai (melee)', 0x8a1a1a, start('samurai'));
    mk(colX[2], rowY(5), 'Olive Oil 1:00', 0x6b7a1a, start('oliveoil'));
    mk(colX[3], rowY(5), 'Finish daily quests', 0xb35f10, () => {
      // Every counter +1000 in the sandbox (rank +10 for the rank-up sets), so
      // every row on the QUESTS tab shows its CLAIM button.
      const s = saveStorage();
      const q = new QuestService(s, 'debug');
      q.ensure(Date.now());
      for (const f of STAT_FIELDS) q.record(f, 1000, Date.now());
      s.setItem('hambi.waves', String(parseInt(s.getItem('hambi.waves') ?? '0', 10) + 1000));
      const rank = new RankService(s);
      rank.setRank(Math.min(MAX_RANK, rank.rank() + 10));
      enqueueBanner('Debug', 'quest counters +1000 (sandbox)');
    });
    // Revision 11: the nacho stages, the wave-10 launch and Space Debris.
    mk(colX[0], rowY(7), 'Rocket Rage', 0x8a1a1a, () => {
      // The launch is wave 10; fast-forward (the HUD's 2x) gets there. Winning
      // it pays Space Debris and shows the claim modal.
      const p = progress();
      p.setHubCompleted(8);
      this.scene.start('GameScene', { levelId: DEBRIS_LEVEL_ID });
      this.scene.launch('HudScene', { levelId: DEBRIS_LEVEL_ID, debug: true });
    });
    mk(colX[1], rowY(7), 'Debris 2:00', 0x5a6b7a, start('debris'));
    mk(colX[2], rowY(7), 'Clear debris', 0x4a4f57, () => {
      const p = progress();
      while (p.takeDebris());
      enqueueBanner('Debug', 'debris shelf emptied');
    });
    mk(colX[3], rowY(7), 'Storage · debris', 0x5a6b7a, () => {
      const p = progress();
      p.awardDebris();
      p.awardDebris();
      p.awardDebris();
      this.scene.start('StorageScene', { from: 'DemoScene' });
    });
    mk(colX[4], rowY(7), 'Debris popup', 0x5a6b7a, () => enqueueDevPopup('debris'));

    // Revision 12: the chapter finale, Prince Nacho and the Moon Stone.
    mk(colX[0], rowY(8), 'Space and Time', 0x6b1a8a, () => {
      // The finale, from the top: two launches (waves 10 and 20) and the boss
      // on wave 30. The HUD's 2x gets there.
      progress().setHubCompleted(9);
      this.scene.start('GameScene', { levelId: MOONSTONE_LEVEL_ID });
      this.scene.launch('HudScene', { levelId: MOONSTONE_LEVEL_ID, debug: true });
    });
    mk(colX[1], rowY(8), 'Moon Stone 30:00', 0x9d7bff, start('moonstone'));
    mk(colX[2], rowY(8), 'Storage · stone', 0x9d7bff, () => {
      progress().claimMoonstone();
      this.scene.start('StorageScene', { from: 'DemoScene' });
    });
    mk(colX[3], rowY(8), 'Victory cutscene', 0x6b1a8a, () => {
      // The ten-second send-off on its own, with no level to win first.
      playPrinceNachoCutscene(this, () => enqueueBanner('Debug', 'cutscene finished'));
    });
    mk(colX[4], rowY(8), 'Clear stone', 0x4a4f57, () => {
      saveStorage().removeItem('hambi.moonstone');
      enqueueBanner('Debug', 'Moon Stone un-claimed (sandbox)');
    });

    // The Fungi Forest (specs 2026-10-20 → 2026-10-27). Eleven levels no
    // longer fit a button each, so the chapter button opens EVERY level (the
    // sandbox cursor at 21) and only the finale's four get their own; the
    // tab keeps its eleven rows for the Linux-font overlap e2e.
    const fungi = HUB_CHAPTERS[1];
    mk(colX[0], rowY(9), 'Fungi · all open', 0xa3500f, () => {
      unlockGates();
      progress().setHubCompleted(HUB_LEVELS.length);
      this.scene.start('HubChapterScene', { chapter: fungi.number });
    });
    fungi.levels.slice(7).forEach((l, i) => {
      const [x, y] = i < 3 ? [colX[i + 1], rowY(9)] : [colX[1], rowY(10)];
      mk(
        x,
        y,
        `Play ${l.name.split(' ')[0] === 'The' ? l.name.split(' ')[1] : l.name.split(' ')[0]}`,
        0x1a6b1a,
        () => {
          this.scene.start('GameScene', { levelId: l.id });
          this.scene.launch('HudScene', { levelId: l.id, debug: true });
        },
      );
    });
    // The Fungi items (spec 2026-10-24) and the Token of Shroom (2026-10-27).
    mk(colX[0], rowY(10), 'Storage · shrooms', 0xa3500f, () => {
      const p = progress();
      p.awardFungiItem('shroom', 2);
      p.awardFungiItem('snowshroom');
      p.claimTokenOfShroom();
      this.scene.start('StorageScene', { from: 'DemoScene' });
    });
    mk(colX[4], rowY(9), 'Fungi Arena', 0xa3500f, () => {
      this.scene.start('GameScene', { levelId: FUNGI_ARENA_LEVEL_ID });
      this.scene.launch('HudScene', { levelId: FUNGI_ARENA_LEVEL_ID, arena: 'fungi' });
    });
    // The Golden Shroom (spec 2026-10-27): granted in the sandbox, then the
    // Fungi Arena — place him, send a wave, and SHROOM FRENZY goes 25 s later.
    mk(colX[2], rowY(10), 'Golden Shroom · arena', 0xc9a227, () => {
      progress().grantTower('goldenshroom');
      this.scene.start('GameScene', { levelId: FUNGI_ARENA_LEVEL_ID });
      this.scene.launch('HudScene', { levelId: FUNGI_ARENA_LEVEL_ID, arena: 'fungi' });
    });
    mk(colX[3], rowY(10), 'Shroometron cutscene', 0x5a3a1e, () => {
      playShroometronCutscene(this, () => enqueueBanner('Debug', 'cutscene finished'));
    });
    mk(colX[4], rowY(10), 'Golden Shroom prize', 0xc9a227, () =>
      this.scene.start('GameScene', { levelId: 119, demo: 'win' }),
    );

    mk(colX[4], rowY(5), 'Reset quests', 0x4a4f57, () => {
      const s = saveStorage();
      s.removeItem('hambi.quests');
      s.removeItem('hambi.stats');
      enqueueBanner('Debug', 'quests + stats cleared (sandbox)');
    });
  }

  // ── Events tab ─────────────────────────────────────────────────────────────

  /**
   * The Origin Event (specs 2026-10-06, -07, -08) and the capsules: both
   * chapters' levels straight in, their menus and Test Arenas, the shards, the
   * coins, the Evil Orb's send-off. Its own tab since its nine levels outgrew
   * the HUB tab's rows.
   */
  private buildEvents(parent: Phaser.GameObjects.Container): void {
    parent.add(
      this.add.text(
        70,
        112,
        'EVENTS — the Origin Event (Above the Clouds · The Dark Shadows) and the capsules.\n' +
          'Level buttons play the real level; the shelves fill the sandbox save; arenas carry Friendly + Disable boss abilities.',
        { fontSize: '13px', color: '#9aa6ad', lineSpacing: 4 },
      ),
    );
    const mk = (x: number, y: number, label: string, color: number, onClick: () => void) =>
      parent.add(this.makeButton(x, y, label, color, onClick));
    const colX = [120, 360, 600, 840, 1080];
    const rowY = (r: number) => 196 + r * 64;
    const progress = () => new ProgressService(saveStorage(), LAST_LEVEL_ID);
    const unlockHub = () => {
      const s = saveStorage();
      const flags = new Set<string>(['hub', 'potionshop']);
      try {
        for (const f of JSON.parse(s.getItem('hambi.flags') ?? '[]') as unknown[])
          if (typeof f === 'string') flags.add(f);
      } catch {
        /* fresh */
      }
      s.setItem('hambi.flags', JSON.stringify([...flags]));
    };
    const play = (id: number) => () => {
      this.scene.start('GameScene', { levelId: id });
      this.scene.launch('HudScene', { levelId: id, debug: true });
    };
    const arena = (id: number, kind: 'moon' | 'shadows' | 'clouds') => () => {
      this.scene.start('GameScene', { levelId: id });
      this.scene.launch('HudScene', { levelId: id, arena: kind });
    };

    // Row 0 — the menus and the three Hub arenas.
    mk(colX[0], rowY(0), 'Origin · Clouds', 0x3a6a9a, () => {
      unlockHub();
      this.scene.start('OriginChapterScene', { chapter: 'clouds' });
    });
    mk(colX[1], rowY(0), 'Origin · Shadows', 0x7a2a1a, () => {
      unlockHub();
      this.scene.start('OriginChapterScene', { chapter: 'shadows' });
    });
    mk(colX[2], rowY(0), 'Cloud Arena', 0x3a6a9a, arena(CLOUD_ARENA_LEVEL_ID, 'clouds'));
    mk(colX[3], rowY(0), 'Shadow Arena', 0x7a2a1a, arena(SHADOW_ARENA_LEVEL_ID, 'shadows'));
    mk(colX[4], rowY(0), 'Moon Arena', 0x8a1a1a, arena(MOON_ARENA_LEVEL_ID, 'moon'));

    // Rows 1–2 — every level, straight in: Above the Clouds, then The Dark
    // Shadows. "Shadowing Meadow" / "Shadowing Temple" share a first word, so
    // the label is the LAST word of the name ("Quest for the Evil Orb" → Orb).
    ORIGIN_CHAPTERS.forEach((chapter, row) => {
      chapter.levels.forEach((l, i) => {
        const words = l.name.replace(/^The /, '').split(' ');
        const color = chapter.id === 'clouds' ? 0x2a5a8a : 0x7a2a1a;
        mk(colX[i], rowY(1 + row), `Play ${words[words.length - 1]}`, color, play(l.id));
      });
    });
    // Above the Clouds now fills its row (Heaven's Peak took the fifth slot),
    // so the Events tab moved to row 4's free end.
    mk(colX[4], rowY(4), 'Events tab', 0x8a6d00, () => {
      unlockHub();
      this.scene.start('HubScene', { tab: 'events' });
    });

    // Row 3 — the shards and the shelves.
    const start = (kind: EffectKind) => () => {
      const r = progress().startEffect(kind, undefined, Date.now());
      enqueueBanner('Debug', r.ok ? `${kind} started` : `${kind}: ${r.reason}`);
    };
    mk(colX[0], rowY(3), 'Light Shard 2:00:00', 0x6a8aa8, start('lightshard'));
    mk(colX[1], rowY(3), 'Dark Shard 2:00:00', 0xd8263a, start('darkshard'));
    mk(colX[2], rowY(3), 'Storage · light', 0x6a8aa8, () => {
      // Two of each shard, side by side, so the white and the red can be compared.
      const p = progress();
      p.awardLightShard(2);
      p.awardDarkShard(2);
      this.scene.start('StorageScene', { from: 'DemoScene' });
    });
    mk(colX[3], rowY(3), 'Storage · dark', 0xd8263a, () => {
      // Two Dark Shards and the Evil Orb, plus the Variety Orb, so both
      // Ketchuptastic slots show side by side.
      const p = progress();
      p.awardDarkShard(2);
      p.claimEvilOrb();
      p.claimOrb();
      this.scene.start('StorageScene', { from: 'DemoScene' });
    });
    mk(colX[4], rowY(3), 'Evil Orb cutscene', 0xff5a1f, () =>
      playEvilOrbCutscene(
        this,
        { ...EVIL_ORB_DECOR.orb },
        () => {},
        () => enqueueBanner('Debug', 'cutscene finished'),
      ),
    );

    // Row 4 — the coins, and the win screens' coin pills in review mode.
    mk(colX[0], rowY(4), 'Light coins +500', 0x6a8aa8, () => {
      progress().addLightCoins(500);
      enqueueBanner('Debug', 'Light Hambi Coins +500 (sandbox)');
    });
    mk(colX[1], rowY(4), 'Light coin popup', 0x6a8aa8, () => enqueueDevPopup('lightcoins'));
    mk(colX[2], rowY(4), 'Win (Sky Islands)', 0x2a5a8a, () =>
      this.scene.start('GameScene', { levelId: ORIGIN_CHAPTERS[0].levels[3].id, demo: 'win' }),
    );
    mk(colX[3], rowY(4), 'Win (Dark Forest)', 0x7a2a1a, () =>
      this.scene.start('GameScene', { levelId: ORIGIN_CHAPTERS[1].levels[3].id, demo: 'win' }),
    );

    // Row 5 — the Capybara Event's capsules.
    mk(colX[0], rowY(5), 'Storage · capsules', 0x8a5430, () => {
      const p = progress();
      p.addCapycoins(500);
      p.buyCapsule(0, 0);
      p.storeCapsule();
      p.buyCapsule(0, 0);
      p.storeCapsule();
      this.scene.start('StorageScene', { from: 'DemoScene' });
    });
    mk(colX[1], rowY(5), 'Capy towers · arena', 0x8a5430, () => {
      // All three owned and equipped, in the Moon Arena where anything can be
      // thrown at them.
      const p = progress();
      for (const t of CAPSULE_TOWERS) p.awardTower(t as Parameters<typeof p.awardTower>[0]);
      p.setEquipped(CAPSULE_TOWERS, CAPSULE_TOWERS);
      this.scene.start('GameScene', { levelId: MOON_ARENA_LEVEL_ID });
      this.scene.launch('HudScene', { levelId: MOON_ARENA_LEVEL_ID, arena: 'moon' });
    });

    // The Origin shops (spec 2026-10-12). The sandbox gets the coins to buy.
    mk(colX[2], rowY(5), 'Dark Shop +1000', 0x15151c, () => {
      unlockHub();
      progress().addDarkCoins(1000);
      this.scene.start('OriginShopScene', { shop: 'dark' });
    });
    mk(colX[3], rowY(5), 'Light Shop +1000', 0x8a7aa8, () => {
      unlockHub();
      progress().addLightCoins(1000);
      this.scene.start('OriginShopScene', { shop: 'light' });
    });
    mk(colX[4], rowY(5), 'Dark towers · arena', 0x15151c, () => {
      // All three owned and equipped, in the Cloud Arena — where Lord
      // Turtleshell can be sent at them.
      const p = progress();
      p.addDarkCoins(900);
      for (const t of DARK_SHOP_TOWERS) p.purchaseTowerWithDarkCoins(t, DARK_SHOP_PRICES[t]);
      p.setEquipped([...DARK_SHOP_TOWERS], [...DARK_SHOP_TOWERS]);
      this.scene.start('GameScene', { levelId: CLOUD_ARENA_LEVEL_ID });
      this.scene.launch('HudScene', { levelId: CLOUD_ARENA_LEVEL_ID, arena: 'clouds' });
    });

    // Row 6 — the Light Orb, the chests and the Light Shop's towers
    // (spec 2026-10-13).
    mk(colX[0], rowY(6), 'Light Orb cutscene', 0x3a7aaa, () => {
      const { centre } = HEAVENS_PEAK_RING;
      playOrbCutscene(
        this,
        LIGHT_ORB_LOOK,
        { x: centre.x, y: centre.y - 6, size: 84 },
        () => {},
        () => enqueueBanner('Debug', 'cutscene finished'),
      );
    });
    mk(colX[1], rowY(6), 'Storage · chests', 0x6a4aa0, () => {
      // Two of each chest and the Light Orb beside the Evil Orb.
      const p = progress();
      p.awardChest('dark', 2);
      p.awardChest('light', 2);
      p.claimLightOrb();
      p.claimEvilOrb();
      this.scene.start('StorageScene', { from: 'DemoScene' });
    });
    mk(colX[2], rowY(6), 'Light towers · arena', 0x8a7aa8, () => {
      // All three owned and equipped, in the Cloud Arena.
      const p = progress();
      p.addLightCoins(900);
      for (const t of LIGHT_SHOP_TOWERS) p.purchaseTowerWithLightCoins(t, LIGHT_SHOP_PRICES[t]);
      p.setEquipped([...LIGHT_SHOP_TOWERS], [...LIGHT_SHOP_TOWERS]);
      this.scene.start('GameScene', { levelId: CLOUD_ARENA_LEVEL_ID });
      this.scene.launch('HudScene', { levelId: CLOUD_ARENA_LEVEL_ID, arena: 'clouds' });
    });
    // The opening on its own: theatre only, nothing paid.
    mk(colX[3], rowY(6), 'Open dark chest', 0x15151c, () =>
      openChest(this, { kind: 'dark', prize: 'shards', onDone: () => {} }),
    );
    mk(colX[4], rowY(6), 'Open light chest', 0x8a7aa8, () =>
      openChest(this, { kind: 'light', prize: 'coins', onDone: () => {} }),
    );

    // Row 7 — feedback and the admin inbox (spec 2026-10-24). Both need a
    // session: the sandbox shows "Sign in…" and "admins only".
    mk(colX[0], rowY(7), 'Feedback panel', FEEDBACK_GREEN, () => openFeedbackPanel(this));
    mk(colX[1], rowY(7), 'Admin inbox', 0x262d3c, () => this.scene.start('AdminInboxScene'));
  }

  // ── Stats tab ──────────────────────────────────────────────────────────────

  private buildStats(parent: Phaser.GameObjects.Container): void {
    // Towers (left). The roster only ever grows, and a fixed number of
    // sub-columns keeps expiring: one long list ran off the canvas, then two
    // sub-columns did the same the moment the roster passed 16 (the Fire Mage
    // and Aqua Sorcerer pushed the taller column's last line to y=742). So the
    // COLUMN COUNT is derived rather than fixed — enough columns that the
    // tallest one fits the budget below, which keeps working as towers are
    // added or gain an Evolution instead of needing this comment rewritten.
    // Gap and indent are tight on purpose: every pixel spent between columns is
    // a pixel the tier lines lose, and those already downscale to fit (a third
    // column costs them ~30% of their width, so the slack is worth reclaiming).
    const TOWER_COL_GAP = 16;
    const TOWER_TIER_INDENT = 12;
    const TOWER_AREA_X = 60;
    const TOWER_AREA_W = 700 - TOWER_AREA_X;
    // Vertical advances, shared by the height estimate and the draw loop below
    // so the split can never be computed from different numbers than it renders.
    // The two ADVANCE values are already close to the rendered line heights (14px
    // and 12px text), so headroom is bought from the inter-tower GAP and the
    // start offset — shrinking an advance instead would make consecutive lines
    // touch and trip the overlap check.
    const TOWER_NAME_ADVANCE = 18;
    const TOWER_TIER_ADVANCE = 15;
    const TOWER_ROW_GAP = 3;
    const TOWER_COL_TOP = 130;
    // Deliberate slack under the last row. Linux CI renders these fonts taller
    // than macOS does, so a layout that merely *fits* locally is the recurring
    // ?debug-overlap failure; budgeting 40px of headroom is what stops the next
    // roster addition from being found by CI rather than here.
    const TOWER_COL_HEADROOM = 40;
    const TOWER_COL_BUDGET = 720 - TOWER_COL_TOP - TOWER_COL_HEADROOM;

    parent.add(
      this.add.text(60, 110, 'TOWERS — per tier: cost · damage · fire-rate · range', {
        fontSize: '15px',
        color: GOLD_HEX,
        fontStyle: 'bold',
      }),
    );
    // Measure by HEIGHT, not by count. An evolvable tower prints a fourth tier
    // line, so an even count is not an even column: at 15 towers a 8/7 split by
    // count put four tier-4 towers in the left column and ran Wise Hambi's last
    // line to y=729, nine pixels off a 720px canvas.
    const towerHeight = (t: TowerType): number => {
      const pair = BALANCE.towers[t];
      return TOWER_NAME_ADVANCE + (pair.tier4 ? 4 : 3) * TOWER_TIER_ADVANCE + TOWER_ROW_GAP;
    };
    const heights = TOWER_ORDER.map(towerHeight);
    /**
     * Deal the towers into contiguous columns, none taller than `limit`.
     * Returns null when even one tower on its own busts the limit, which is the
     * only way this can fail to terminate.
     */
    const packColumns = (limit: number): TowerType[][] | null => {
      const cols: TowerType[][] = [[]];
      let run = 0;
      for (let i = 0; i < TOWER_ORDER.length; i++) {
        if (heights[i] > limit) return null;
        if (run + heights[i] > limit) {
          cols.push([]);
          run = 0;
        }
        cols[cols.length - 1].push(TOWER_ORDER[i]);
        run += heights[i];
      }
      return cols;
    };
    // Fewest columns that fit the budget…
    const minCols = (packColumns(TOWER_COL_BUDGET) ?? [TOWER_ORDER]).length;
    // …then the shortest limit that still needs only that many, so the columns
    // come out balanced rather than three-full-and-a-stub.
    let towerCols = packColumns(TOWER_COL_BUDGET) ?? [TOWER_ORDER];
    for (let limit = Math.max(...heights); limit <= TOWER_COL_BUDGET; limit++) {
      const packed = packColumns(limit);
      if (packed && packed.length <= minCols) {
        towerCols = packed;
        break;
      }
    }
    const colCount = towerCols.length;
    const colW = (TOWER_AREA_W - (colCount - 1) * TOWER_COL_GAP) / colCount;
    const TOWER_COL_MAX_W = colW - TOWER_TIER_INDENT;
    towerCols.forEach((col, colIdx) => {
      const colX = TOWER_AREA_X + colIdx * (colW + TOWER_COL_GAP);
      let y = TOWER_COL_TOP;
      for (const t of col) {
        const pair = BALANCE.towers[t];
        // The name line needs the same width guard the tier lines have: it is
        // the widest thing in the column ("Wise Hambi  [global-freeze]"), it is
        // NOT indented, and once the roster needed a third column it was long
        // enough to run into the next column's names.
        const name = this.add.text(colX, y, `${TOWER_NAMES[t]}  [${pair.tier1.behavior}]`, {
          fontSize: '14px',
          color: '#ffffff',
          fontStyle: 'bold',
        });
        if (name.width > colW) name.setScale(colW / name.width);
        parent.add(name);
        y += TOWER_NAME_ADVANCE;
        [pair.tier1, pair.tier2, pair.tier3, ...(pair.tier4 ? [pair.tier4] : [])].forEach(
          (s, i) => {
            const cost = i === 0 ? `$${s.cost}` : `+$${s.upgradeCost}`;
            const dmg = s.damage > 0 ? `dmg ${s.damage}` : 'no dmg';
            const rate =
              s.fireRate > 0
                ? `${Math.round(s.fireRate * 100) / 100}/s`
                : s.behavior === 'melee'
                  ? s.strikeWait
                    ? `every ${s.strikeWait}s`
                    : 'nonstop' // strikeWait 0 — the Ninja Hambi
                  : 'aura';
            const dps =
              s.fireRate > 0 && s.damage > 0 ? `  (${Math.round(s.damage * s.fireRate)} dps)` : '';
            const extra = towerSpecial(s, t);
            const line = this.add.text(
              colX + TOWER_TIER_INDENT,
              y,
              `T${i + 1}: ${cost}  ${dmg}  ${rate}  rng ${s.range}${dps}${extra ? `  · ${extra}` : ''}`,
              { fontSize: '12px', color: '#cfd3dc' },
            );
            if (line.width > TOWER_COL_MAX_W) line.setScale(TOWER_COL_MAX_W / line.width);
            parent.add(line);
            y += TOWER_TIER_ADVANCE;
          },
        );
        y += TOWER_ROW_GAP;
      }
    });

    // Enemies (right).
    parent.add(
      this.add.text(720, 110, 'ENEMIES', { fontSize: '15px', color: GOLD_HEX, fontStyle: 'bold' }),
    );
    parent.add(
      this.add.text(720, 134, 'Each enemy that reaches the end costs 1 life (bosses cost more).', {
        fontSize: '12px',
        color: '#9aa6ad',
      }),
    );
    // Three sub-columns, like the TOWERS list. A single 23-row column had to be
    // squeezed to a 13px pitch to keep the blocks below on-canvas, which left
    // barely 1px between rows — one font-metric change on CI away from an
    // overlap failure. Two columns fixed that until The Moon's roster pushed
    // the schedule below off the canvas (749px); three keep the 16px pitch
    // with ~60px of headroom to spare. Rows are compacted to fit the narrower
    // column (scaled down where a long name overflows) without downscaling
    // into illegibility; the per-enemy prose lives in the CHAPTERS tab.
    const ENEMY_COL_X = [720, 906, 1092];
    const ENEMY_COL_W = 180;
    const enemyThird = Math.ceil(ENEMY_ORDER.length / 3);
    let enemyBottom = 162;
    [
      ENEMY_ORDER.slice(0, enemyThird),
      ENEMY_ORDER.slice(enemyThird, enemyThird * 2),
      ENEMY_ORDER.slice(enemyThird * 2),
    ].forEach((col, colIdx) => {
      let ey = 162;
      for (const e of col) {
        const s = BALANCE.enemies[e];
        const lives = s.livesCost ?? 1;
        // Only the exceptions are spelled out — every enemy costs 1 life and
        // most carry no hex, so printing those on every row was pure noise.
        const dmg = lives >= 99 ? '  ·  ALL lives' : lives > 1 ? `  ·  ${lives} lives` : '';
        const stun = s.stun ? `  ·  stun ${s.stun.duration}/${s.stun.cooldown}s` : '';
        const row = this.add.text(
          ENEMY_COL_X[colIdx],
          ey,
          `${ENEMY_NAMES[e]}  ${s.hp}hp · ${s.speed}spd · $${s.bounty}${dmg}${stun}`,
          { fontSize: '11px', color: '#cfd3dc' },
        );
        // Long names (e.g. The Dripstone Driller) must not run into the next
        // column or off the canvas.
        if (row.width > ENEMY_COL_W) row.setScale(ENEMY_COL_W / row.width);
        parent.add(row);
        // 14, not 16: the Origin Event's three enemies (2026-10-06) added a
        // row to every column, and the OTHER SOURCES block below ran 4px off
        // the canvas on CI's taller Linux fonts; the Fungi finale's three
        // (2026-10-27) did it again at 15 (723 > 720 on CI).
        ey += 14;
      }
      enemyBottom = Math.max(enemyBottom, ey);
    });
    const ey = enemyBottom;

    // DAMAGE RULE and UNLOCK SCHEDULE sit side by side below the enemies list
    // (two sub-columns of the ENEMIES column) instead of stacked — stacking
    // them summed both blocks' font-metric variance and repeatedly pushed the
    // schedule below the fold as either block grew; side-by-side bounds the
    // combined height to whichever block is taller, with real headroom to
    // spare for future copy. Both are word-wrapped so they can't run into
    // each other or off the canvas edge regardless of font metrics.
    const subColY = ey + 8;
    const subColGap = 24;
    const subColW = (1280 - 720 - subColGap - 8) / 2; // ≈ 264 each

    const damageRule = this.add.text(
      720,
      subColY,
      // Each bullet fits ONE line of the sub-column: wrapped bullets were the
      // slack the OTHER SOURCES block below needed once the Dark Shadows'
      // enemies grew the roster (2026-10-07).
      'DAMAGE RULE\n' +
        '• Each upgrade ~3× stronger.\n' +
        '• Tier 2 ≈ 2× base price, tier 3 ≈ 4×.\n' +
        '• DPS = damage × fire-rate.\n' +
        '• Cheese: slow aura, no damage.\n' +
        '• Bacon/Sauce burn; Sauce: vulnerable.',
      { fontSize: '11px', color: '#9aa6ad', lineSpacing: 3, wordWrap: { width: subColW } },
    );
    parent.add(damageRule);

    // Unlock schedule (episode → tower introduced) — right sub-column, level
    // with DAMAGE RULE rather than stacked beneath it.
    const scheduleLines = Object.keys(TOWER_UNLOCKS)
      .map(Number)
      .sort((a, b) => a - b)
      .map((id) => `L${id}: ${TOWER_UNLOCKS[id].map((t) => TOWER_NAMES[t]).join(', ')}`)
      .join('\n');
    // Two ways in for a non-episode tower: the Shop still sells six, and the
    // Restaurant's takeout bag rolls the other five. Anything in NEITHER list
    // would be unobtainable, so the gap line calls that out loudly.
    const shopLines = SHOP_SALE_TOWERS.map(
      (t) => `SHOP: ${TOWER_NAMES[t]} — ${SHOP_PRICES[t]} coin${SHOP_PRICES[t] === 1 ? '' : 's'}`,
    ).join('\n');
    // ONE wrapping string, like the capsule's line below: the Dark Shadows'
    // four enemies (2026-10-07) grew the roster above to within a pixel of the
    // canvas on macOS, and five BAG rows were the slack.
    const bagLines =
      'BAG: ' +
      TAKEOUT_BAG_ODDS.map(
        (s) => `${TOWER_NAMES[s.tower].replace(/ Hambi$/, '')} ${oddsLabel(s)}`,
      ).join(' · ');
    // The Hub's Market is the third way in (Lightning, Spear and Bow Hambi).
    // Short names: three entries on one line must not outgrow the column on
    // CI's wider Linux fonts.
    const marketLine = `MARKET: ${MARKET_TOWERS.map((t) => `${TOWER_NAMES[t].replace(' Hambi', '')} ${SHOP_PRICES[t]}c`).join(' · ')}`;
    // The Capybara Event's capsule is the fifth way in, and the only way to the
    // seven capy Hambis. SHORT names and bare percentages, run together into
    // ONE wrapping string, for the same reason the Market's line is short —
    // and then some. This column is `subColW` (≈264 px) wide with wordWrap on,
    // so what matters is not how many `\n` you write but how many lines the
    // text WRAPS to, and the whole OTHER SOURCES block has only ~45 px of room
    // below it before it leaves the 720 px canvas. Listing the seven towers one
    // per row pushed the block to y 792 and failed the overlap e2e on CI's
    // wider Linux fonts. Keep this string at roughly 78 characters — the length
    // of the three-tower version that fitted — and it stays at two lines.
    const capsuleShort = (t: TowerType) =>
      TOWER_NAMES[t].replace(/ (Hambi|Capy)$/, '').replace(/\s+/g, '');
    // Since the Capybara Event closed (2026-10-06) a capsule is no longer
    // bought, only given (`/give`) or opened from Storage — so no price or
    // timer, and the line is shorter than ever.
    const capsuleLine =
      'CAPSULE (/give · Storage): ' +
      CAPSULE_ODDS.map((c) => `${capsuleShort(c.tower)}${Math.round(c.weight * 100)}`).join(' ');
    const sourced = new Set<TowerType>([
      ...TAKEOUT_BAG_ODDS.map((s) => s.tower),
      ...SHOP_SALE_TOWERS,
      ...MARKET_TOWERS,
      ...CAPSULE_TOWERS,
      ...GOLDEN_BAG_TOWERS,
      // The Origin shops (specs 2026-10-12 / -13): uncounted, their six towers
      // filled a "NO SOURCE" line that pushed this block off the canvas on CI.
      ...DARK_SHOP_TOWERS,
      ...LIGHT_SHOP_TOWERS,
    ]);
    const orphans = SHOP_TOWERS.filter((t) => !sourced.has(t));
    const legacyLine =
      orphans.length > 0
        ? `⚠ NO SOURCE (${orphans.map((t) => TOWER_NAMES[t]).join(', ')}): ` +
          'owned saves keep them; nothing grants them now'
        : '';
    // ONE line for both bags — the Golden one must not add a row (see capsuleLine).
    const bagCostLine =
      `RESTAURANT: bag ${BAG_COST}c/${BAG_PREP_MS / 60000}m (dup ${DUPLICATE_REFUND}) · ` +
      `golden ${GOLDEN_BAG_COST}c/${GOLDEN_BAG_PREP_MS / 60000}m (dup ${GOLDEN_DUPLICATE_REFUND})`;
    // ONE line for every win grant: King Hambi made it three, and three lines
    // pushed this block off the bottom of the canvas (see capsuleLine).
    const grantLines =
      'WIN: ' +
      Object.keys(TOWER_GRANTS_ON_WIN)
        .map(Number)
        .sort((a, b) => a - b)
        .map(
          (id) =>
            `L${id} ${TOWER_GRANTS_ON_WIN[id].map((t) => TOWER_NAMES[t].replace(/^The /, '').split(' ')[0]).join(', ')}`,
        )
        .join(' · ');
    // The fourth way to get a tower: an Arena challenge — neither sold, nor
    // introduced by an episode, nor dropped by a boss.
    const challengeLine =
      `ARENA: ${TOWER_NAMES[SHREK_CHALLENGE.reward]} — defeat ${SHREK_CHALLENGE.count} ` +
      `${ENEMY_NAMES[SHREK_CHALLENGE.enemyType]}s in one wave`;
    // Split in two so neither block runs off the canvas: the episode / shop
    // schedule fills the right sub-column, and the other sources (bags, win
    // grants, the arena challenge) sit UNDER the damage rule on the left. One
    // tall column reached y=749 once The Moon's roster grew the enemies list.
    const small = {
      fontSize: '10px',
      color: '#9aa6ad',
      lineSpacing: 1,
      wordWrap: { width: subColW },
    };
    parent.add(
      this.add.text(
        720 + subColW + subColGap,
        subColY,
        `UNLOCK SCHEDULE (episode → tower)\n${scheduleLines}\n${shopLines}\n${bagCostLine}`,
        small,
      ),
    );
    parent.add(
      this.add.text(
        720,
        damageRule.y + damageRule.height + 8,
        // Paired up so each pair wraps as ONE run rather than starting a fresh
        // line apiece — the same fold as above (2026-10-07).
        [
          'OTHER SOURCES',
          `${bagLines} · ${marketLine}`,
          capsuleLine,
          legacyLine,
          `${grantLines} · ${challengeLine}`,
        ]
          .filter((l) => l.length > 0)
          .join('\n'),
        small,
      ),
    );
  }

  // ── Chapters tab ─────────────────────────────────────────────────────────────

  private buildChapters(parent: Phaser.GameObjects.Container): void {
    parent.add(
      this.add.text(60, 110, 'CHAPTERS — coin rewards & per-episode detail', {
        fontSize: '15px',
        color: GOLD_HEX,
        fontStyle: 'bold',
      }),
    );

    // Four columns since Chapter 3 · Ep VI–VII took the campaign to 22
    // episodes — three columns overflowed the last one off the bottom, and the
    // last column has no next column to spill into.
    const colX = [40, 350, 660, 970];
    const colTop = 142;
    // 665, not 692: Linux CI's taller font metrics need ~40px of slack at the
    // fold (see the recurring ?debug-overlap failure), and four columns leave
    // ample capacity to break early.
    const colBottom = 665;
    let col = 0;
    let y = colTop;
    // Flow text top-to-bottom, spilling into the next column when one fills.
    const place = (
      dx: number,
      text: string,
      style: Phaser.Types.GameObjects.Text.TextStyle,
      dy: number,
      wrap = false,
    ) => {
      // Build the text first so its REAL height decides both the advance and
      // the column break. `dy` is only a MINIMUM: Linux CI renders the same
      // font taller than macOS, and advancing by a hard-coded dy is what let
      // an episode title land on the previous episode's roster line (the
      // recurring ?debug-overlap failure). Measured height wins whenever it is
      // larger, for wrapped and unwrapped text alike.
      const measure = (c: number): Phaser.GameObjects.Text => {
        const t = this.add.text(colX[c] + dx, y, text, style);
        if (wrap) {
          const rightEdge = c + 1 < colX.length ? colX[c + 1] : 1280;
          t.setWordWrapWidth(rightEdge - (colX[c] + dx) - 12);
        }
        return t;
      };
      let t = measure(col);
      const advance = (txt: Phaser.GameObjects.Text) => Math.max(dy, txt.height + 8);
      if (y + advance(t) > colBottom && col < colX.length - 1) {
        t.destroy();
        col += 1;
        y = colTop;
        t = measure(col);
      }
      t.setY(y);
      y += advance(t);
      parent.add(t);
    };

    for (const chapter of CHAPTERS) {
      const levels = levelsInChapter(chapter);
      const chapterCoins = levels.reduce((sum, l) => sum + l.coinReward, 0);
      // Split across two lines: the four columns are ~310px wide, too narrow
      // for the old single-line header.
      place(
        0,
        `CHAPTER ${chapter}  ·  ${levels.length} episodes`,
        { fontSize: '14px', color: GOLD_HEX, fontStyle: 'bold' },
        17,
      );
      place(0, `+${chapterCoins} coins total`, { fontSize: '12px', color: GOLD_HEX }, 20);
      for (const level of levels) {
        const boss = hasBoss(level);
        const waveCount = level.waves.length;
        const enemyTypes = Array.from(
          new Set(level.waves.flatMap((w) => w.groups.map((g) => g.enemyType))),
        );
        // Cap the roster line: a full 7-enemy list wrapped to four lines in a
        // 310px column, and its height is exactly what the fold and the
        // next-episode spacing cannot absorb on CI's taller fonts. Three names
        // plus a count keeps every episode block to a bounded two lines; the
        // STATS tab carries the full per-enemy detail.
        const ROSTER_SHOWN = 2;
        const names = enemyTypes.map((e) => ENEMY_NAMES[e]);
        const enemyList =
          names.length > ROSTER_SHOWN
            ? `${names.slice(0, ROSTER_SHOWN).join(', ')}  +${names.length - ROSTER_SHOWN} more`
            : names.join(', ') || '—';
        place(
          16,
          `Ep ${level.episode ?? level.id}: ${level.name}${boss ? '   ★ BOSS' : ''}`,
          { fontSize: '13px', color: '#ffffff', fontStyle: 'bold' },
          16,
        );
        // One compact line, abbreviated to fit a ~266px column: two full-width
        // lines per episode pushed the 22-episode campaign past the bottom of
        // the fourth column. Same four facts, ~40% of the height.
        place(
          32,
          `+${level.coinReward}c · ${waveCount}w · $${level.startCash} · ${level.startLives} lives`,
          { fontSize: '11px', color: '#cfd3dc' },
          14,
        );
        // dy 28 = two 11px lines with room to spare, so even if the wrapped
        // height is under-reported the next title still clears this block.
        place(32, `enemies: ${enemyList}`, { fontSize: '11px', color: '#9aa6ad' }, 28, true);
      }
      y += 8;
    }
    // NOTE: the start-screen SHOP price list used to render here, but as
    // Chapter 3 grew to five episodes its tail overflowed the third column off
    // the bottom of the canvas on Linux CI's taller fonts (the recurring
    // ?debug-overlap failure). It is fully duplicated on the STATS tab's
    // UNLOCK SCHEDULE (the `SHOP: <tower> — N coins` lines), so it lives there
    // now and the CHAPTERS tab keeps generous headroom.
  }

  // ── Shared ─────────────────────────────────────────────────────────────────

  private makeButton(
    x: number,
    y: number,
    label: string,
    color: number,
    onClick: () => void,
  ): Phaser.GameObjects.Container {
    const w = 210;
    const h = 48;
    const bg = this.add
      .rectangle(0, 0, w, h, color)
      .setStrokeStyle(2, 0xffffff, 0.4)
      .setInteractive({ useHandCursor: true });
    const txt = this.add
      .text(0, 0, label, { fontSize: '17px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5);
    bg.on('pointerover', () => bg.setStrokeStyle(2, GOLD, 1));
    bg.on('pointerout', () => bg.setStrokeStyle(2, 0xffffff, 0.4));
    bg.on('pointerup', onClick);
    return this.add.container(x, y, [bg, txt]);
  }
}
